// pages/admin/index.js - Complete Admin Analytics Dashboard
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { fetchAdminStats } from './services/adminService';
import { 
  OverviewStats, 
  UserAnalytics, 
  SubscriptionAnalytics, 
  RevenueAnalytics, 
  DocumentAnalytics, 
  PaymentAnalytics 
} from './components/ComprehensiveDashboard';
import GrantPremiumAccess from './components/GrantPremiumAccess';
import { collection, getDocs, doc, getDoc, query, where } from "firebase/firestore";
import { db } from "../../config/firebaseConfig";
import './Admin.css';

const Admin = () => {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const { user, setTempUser } = useAuth();
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('dashboard');

  // User context switching state
  const [userContext, setUserContext] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [adminEmail, setAdminEmail] = useState(null);

  useEffect(() => {
    const checkAdminAccess = async () => {
      if (!user) {
        navigate('/login');
        return;
      }

      try {
        const adminData = await fetchAdminStats(user.uid);
        setStats(adminData);
        setLoading(false);
      } catch (err) {
        console.error('Error fetching admin data:', err);
        setError('You do not have permission to access the admin panel or there was an error fetching data.');
        setTimeout(() => navigate('/dashboard'), 3000);
        setLoading(false);
      }
    };

    const fetchAdminEmail = async () => {
      try {
        const docRef = doc(db, "admin", "IStA6fJzjiHovjcz6B0V");
        const docSnap = await getDoc(docRef);
        if (docSnap.exists()) {
          const data = docSnap.data();
          setAdminEmail(data.adminUser[0]);
        }
      } catch (error) {
        console.error("Error fetching admin email: ", error);
      }
    };

    checkAdminAccess();
    fetchAdminEmail();

    // Check for stored user context
    const storedUserId = localStorage.getItem('selectedUserId');
    if (storedUserId) {
      handleUserSelect(storedUserId);
    }
  }, [user, navigate]);

  const handleUserSelect = async (userId) => {
    const userDoc = await getDoc(doc(db, "users", userId));
    const selectedUser = { id: userId, ...userDoc.data() };
    setUserContext(selectedUser);
    setTempUser({ uid: userId, ...userDoc.data() });
    localStorage.setItem('selectedUserId', userId);
    setActiveTab('userContext');
  };

  const handleExitUserContext = () => {
    setUserContext(null);
    setTempUser(null);
    localStorage.removeItem('selectedUserId');
    setActiveTab('dashboard');
  };

  const handleSearch = async (e) => {
    e.preventDefault();
    if (searchQuery.trim() === '') return;

    const usersCollection = collection(db, "users");
    const q = query(usersCollection, where('email', '==', searchQuery));
    const querySnapshot = await getDocs(q);

    const results = querySnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
    setSearchResults(results);
  };

  const refreshStats = async () => {
    setLoading(true);
    try {
      // Clear cache to force refresh
      const adminData = await fetchAdminStats(user.uid);
      setStats(adminData);
    } catch (err) {
      console.error('Error refreshing stats:', err);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="admin-loading">
        <div className="spinner"></div>
        <p>Loading comprehensive dashboard...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="admin-error">
        <h2>Access Denied</h2>
        <p>{error}</p>
        <p>Redirecting to dashboard...</p>
      </div>
    );
  }

  return (
    <div className="admin-dashboard">
      <div className="admin-header">
        <h1>📊 Admin Analytics Dashboard</h1>
        <div className="admin-nav">
          <button 
            className={`admin-nav-btn ${activeTab === 'dashboard' ? 'active' : ''}`} 
            onClick={() => setActiveTab('dashboard')}
          >
            📊 Dashboard
          </button>
          <button 
            className={`admin-nav-btn ${activeTab === 'analytics' ? 'active' : ''}`} 
            onClick={() => setActiveTab('analytics')}
          >
            📈 Analytics
          </button>
          <button 
            className={`admin-nav-btn ${activeTab === 'switchUser' ? 'active' : ''}`} 
            onClick={() => setActiveTab('switchUser')}
          >
            👥 Switch User
          </button>
          <button 
            className={`admin-nav-btn ${activeTab === 'grantAccess' ? 'active' : ''}`} 
            onClick={() => setActiveTab('grantAccess')}
          >
            💎 Grant Premium
          </button>
          {userContext && (
            <button 
              className={`admin-nav-btn ${activeTab === 'userContext' ? 'active' : ''}`} 
              onClick={() => setActiveTab('userContext')}
            >
              👤 {userContext.email}
            </button>
          )}
          <button 
            className="admin-nav-btn refresh-btn" 
            onClick={refreshStats}
            title="Refresh Data"
          >
            🔄 Refresh
          </button>
        </div>
        <p>Welcome, <strong>{user.email}</strong> | Last updated: {new Date().toLocaleTimeString()}</p>
      </div>

      <div className="admin-content">
        {activeTab === 'dashboard' && (
          <div className="admin-grid-enhanced">
            <OverviewStats stats={stats} />
            <UserAnalytics stats={stats} />
            <SubscriptionAnalytics stats={stats} />
          </div>
        )}

        {activeTab === 'analytics' && (
          <div className="admin-grid-enhanced">
            <RevenueAnalytics stats={stats} />
            <DocumentAnalytics stats={stats} />
            <PaymentAnalytics stats={stats} />
          </div>
        )}

        {activeTab === 'switchUser' && (
          <div className="user-search-panel">
            <form onSubmit={handleSearch} className="search-form">
              <input
                type="text"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder="Search by user email"
                className="search-input"
              />
              <button type="submit" className="search-button">🔍 Search</button>
            </form>
            
            <div className="search-results">
              <h2>Search Results</h2>
              {searchResults.length > 0 ? (
                <div className="user-list">
                  {searchResults.map(user => (
                    <div key={user.id} className="user-item">
                      <div className="user-info">
                        <span className="user-email">{user.email}</span>
                        <small className="user-details">
                          {user.subscriptionStatus === 'active' ? '💎 Premium' : '🆓 Free'} | 
                          Joined: {user.createdAt ? new Date(user.createdAt.seconds * 1000).toLocaleDateString() : 'Unknown'}
                        </small>
                      </div>
                      <button 
                        onClick={() => handleUserSelect(user.id)} 
                        className="switch-button"
                      >
                        Switch to User
                      </button>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="no-results">No users found</p>
              )}
            </div>
          </div>
        )}

        {activeTab === 'grantAccess' && (
          <GrantPremiumAccess />
        )}

        {activeTab === 'userContext' && userContext && (
          <UserContextView user={userContext} onExit={handleExitUserContext} />
        )}
      </div>

      {/* Summary Footer */}
      <div className="admin-footer">
        <div className="summary-stats">
          <span>👥 {stats.totalUsers.toLocaleString()} Total Users</span>
          <span>💎 {stats.activeSubscribers.toLocaleString()} Subscribers</span>
          <span>💰 ${stats.revenueMonth_USD} USD + ₹{stats.revenueMonth_INR} INR (Month)</span>
          <span>📄 {(stats.totalResumes + stats.totalCoverLetters).toLocaleString()} Documents</span>
          <span>📈 {stats.paymentSuccessRate}% Payment Success</span>
        </div>
      </div>
    </div>
  );
};

const UserContextView = ({ user, onExit }) => {
  const [resumes, setResumes] = useState([]);
  const [coverLetters, setCoverLetters] = useState([]);
  const navigate = useNavigate();

  useEffect(() => {
    const fetchUserData = async () => {
      try {
        const resumesCollection = collection(db, `users/${user.id}/resumes`);
        const resumesSnapshot = await getDocs(resumesCollection);
        setResumes(resumesSnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() })));

        const coverLettersCollection = collection(db, `users/${user.id}/cover-letters`);
        const coverLettersSnapshot = await getDocs(coverLettersCollection);
        setCoverLetters(coverLettersSnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() })));
      } catch (error) {
        console.error("Error fetching user data:", error);
      }
    };
    
    fetchUserData();
  }, [user]);

  const editResume = (resumeId) => {
    navigate(`/editor/resume/${resumeId}`);
  };

  const editCoverLetter = (coverLetterId) => {
    navigate(`/editor/cover-letter/${coverLetterId}`);
  };

  return (
    <div className="user-context-view">
      <div className="user-context-header">
        <div className="user-context-info">
          <h2>👤 User: {user.email}</h2>
          <div className="user-stats">
            <span className={`status-badge ${user.subscriptionStatus === 'active' ? 'premium' : 'free'}`}>
              {user.subscriptionStatus === 'active' ? '💎 Premium' : '🆓 Free'}
            </span>
            <span>📄 {resumes.length + coverLetters.length} Documents</span>
            <span>📅 Joined: {user.createdAt ? new Date(user.createdAt.seconds * 1000).toLocaleDateString() : 'Unknown'}</span>
          </div>
        </div>
        <button onClick={onExit} className="exit-context-button">❌ Exit Context</button>
      </div>
      
      <div className="user-documents">
        <div className="document-section">
          <h3>📝 Resumes ({resumes.length})</h3>
          {resumes.length > 0 ? (
            <div className="documents-grid">
              {resumes.map(resume => (
                <div key={resume.id} className="document-card">
                  <div className="document-preview">
                    <div className="preview-placeholder">
                      📝 {resume.title || "Untitled Resume"}
                    </div>
                  </div>
                  <div className="document-info">
                    <h4>{resume.title || "Untitled Resume"}</h4>
                    <p>📅 {resume.lastUpdated ? new Date(resume.lastUpdated.seconds * 1000).toLocaleDateString() : "Unknown"}</p>
                    <button onClick={() => editResume(resume.id)} className="edit-button">✏️ Edit</button>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <p className="no-documents">No resumes found</p>
          )}
        </div>
        
        <div className="document-section">
          <h3>📨 Cover Letters ({coverLetters.length})</h3>
          {coverLetters.length > 0 ? (
            <div className="documents-grid">
              {coverLetters.map(letter => (
                <div key={letter.id} className="document-card">
                  <div className="document-preview">
                    <div className="preview-placeholder">
                      📨 {letter.title || "Untitled Cover Letter"}
                    </div>
                  </div>
                  <div className="document-info">
                    <h4>{letter.title || "Untitled Cover Letter"}</h4>
                    <p>📅 {letter.lastUpdated ? new Date(letter.lastUpdated.seconds * 1000).toLocaleDateString() : "Unknown"}</p>
                    <button onClick={() => editCoverLetter(letter.id)} className="edit-button">✏️ Edit</button>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <p className="no-documents">No cover letters found</p>
          )}
        </div>
      </div>
    </div>
  );
};

export default Admin;